import { useState } from "react";
import { useDebounce } from "../utils/hooks/useDebounce";
import { useUserRole } from "../utils/hooks/useUserRole";
import { featureAccess } from "../permissions/access";
import { getProductDetails } from "../services";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ProductTable from "../components/Product/Products/Table";
import ProductFilter from "../components/Product/Products/Filter";
import ProductDrawer, {
  ProductDrawerMode,
} from "../components/Product/Products/ProductDrawer";
import ProductDeleteDialog from "../components/Product/Products/ProductDeleteDialog";
import ProductCategoryListDialog from "../components/Product/Products/ProductCategoryListDialog";
import WebItExportSummary from "../components/Product/Products/WebItExportSummary";
import { useProductCategories } from "../components/Product/Products/useProductCategories";
import { ProductListItem, ProductResponse } from "../types/product.type";
import { ALL_FILTER_VALUE } from "../components/Product/shared/constants";

const SEARCH_DEBOUNCE_MS = 500;

export default function ProductPage() {
  const role = useUserRole();
  const canEditCatalogue = featureAccess.productWrite(role);
  const { categories, categoryNameByCode, reloadCategories } =
    useProductCategories();

  const [search, setSearch] = useState<string>("");
  const [brand, setBrand] = useState<string>("");
  const [categoryCode, setCategoryCode] = useState<string>(ALL_FILTER_VALUE);
  const [status, setStatus] = useState<string>(ALL_FILTER_VALUE);
  const [allowExport, setAllowExport] = useState<string>(ALL_FILTER_VALUE);
  const [triggerFetch, setTriggerFetch] = useState<number>(0);
  const [exportCountVersion, setExportCountVersion] = useState<number>(0);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerMode, setDrawerMode] = useState<ProductDrawerMode>("create");
  const [selectedProductUuid, setSelectedProductUuid] = useState<string>("");
  const [productToDelete, setProductToDelete] =
    useState<ProductResponse | null>(null);
  const [categoryListOpen, setCategoryListOpen] = useState<boolean>(false);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const debouncedBrand = useDebounce(brand, SEARCH_DEBOUNCE_MS);

  const handleOpenCreate = () => {
    setSelectedProductUuid("");
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const handleOpenEdit = (productUuid: string) => {
    setSelectedProductUuid(productUuid);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const handleOpenDuplicate = (productUuid: string) => {
    setSelectedProductUuid(productUuid);
    setDrawerMode("duplicate");
    setDrawerOpen(true);
  };

  // A list row does not say which pricebooks hold the product, and the delete dialog has to
  // name how many lose its price, so the product is read in full before the dialog opens.
  const handleOpenDelete = async (product: ProductListItem) => {
    try {
      const {
        data: { data },
      } = await getProductDetails(product.uuid);
      setProductToDelete(data);
    } catch {
      // The axios interceptor reports the failure; without the pricebooks there is no
      // consequence list to confirm against, so the dialog stays closed.
    }
  };

  const handleCloseDelete = () => {
    setProductToDelete(null);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedProductUuid("");
  };

  const refreshExportCount = () => {
    setExportCountVersion((prev) => prev + 1);
  };

  // A save, a delete or a deactivation can each move a product in or out of the export.
  const handleSaved = () => {
    setTriggerFetch((prev) => prev + 1);
    refreshExportCount();
  };

  // Product counts move with every product save and delete, so the list is read fresh on
  // each open rather than trusting the one the filter loaded.
  const handleOpenCategoryList = () => {
    setCategoryListOpen(true);
    reloadCategories();
  };

  const handleCloseCategoryList = () => {
    setCategoryListOpen(false);
  };

  return (
    <>
      <Box sx={{ padding: "8px 0" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          mb={1}
        >
          <Stack gap={0.5}>
            <Typography variant="h6">สินค้า</Typography>
            <Typography variant="body2" color="text.secondary">
              ทะเบียนสินค้ากลางของบริษัท
            </Typography>
          </Stack>
          {canEditCatalogue && (
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Button variant="outlined" onClick={handleOpenCategoryList}>
                หมวดหมู่สินค้า
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
              >
                เพิ่มสินค้า
              </Button>
            </Stack>
          )}
        </Stack>
        <Paper sx={{ padding: "24px 16px 8px", mb: 2 }}>
          <ProductFilter
            search={search}
            setSearch={setSearch}
            brand={brand}
            setBrand={setBrand}
            categoryCode={categoryCode}
            setCategoryCode={setCategoryCode}
            status={status}
            setStatus={setStatus}
            allowExport={allowExport}
            setAllowExport={setAllowExport}
            categories={categories}
          />
        </Paper>
        <WebItExportSummary refreshKey={exportCountVersion} />
        <ProductTable
          search={debouncedSearch}
          brand={debouncedBrand}
          categoryCode={categoryCode}
          status={status}
          allowExport={allowExport}
          triggerFetch={triggerFetch}
          canEditCatalogue={canEditCatalogue}
          categoryNameByCode={categoryNameByCode}
          onEdit={handleOpenEdit}
          onDuplicate={handleOpenDuplicate}
          onDelete={handleOpenDelete}
          onExportChanged={refreshExportCount}
        />
      </Box>
      <ProductDrawer
        open={drawerOpen}
        mode={drawerMode}
        productUuid={selectedProductUuid || undefined}
        categories={categories}
        onClose={handleCloseDrawer}
        onSaved={handleSaved}
        onDeleted={handleSaved}
        onReloadCategories={reloadCategories}
      />
      <ProductDeleteDialog
        product={productToDelete}
        onClose={handleCloseDelete}
        onDeleted={handleSaved}
        onDeactivated={handleSaved}
      />
      <ProductCategoryListDialog
        open={categoryListOpen}
        categories={categories}
        onClose={handleCloseCategoryList}
        onChanged={reloadCategories}
      />
    </>
  );
}
